import React, { useEffect, useMemo, useState } from 'react';
import apiClient from '../../api/client';
import DataGrid from '../Common/DataGrid';
import { FINOS_CATEGORIES, fetchMarketplaceCategories } from '../../utils/categories';

// finOS sends "N/A" when it has no figure; show a dash instead.
const show = (v) => (v === undefined || v === null || String(v).trim() === '' || String(v).toUpperCase() === 'N/A' ? '–' : v);

/** Products grouped by category, live from finOS. Pick a category to see its products. */
const ProductCatalogue = () => {
  const [categories, setCategories] = useState(FINOS_CATEGORIES);
  const [loadingCats, setLoadingCats] = useState(true);
  const [selected, setSelected] = useState('');
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const list = await fetchMarketplaceCategories(apiClient);
        if (!alive) return;
        setCategories(list);
        // Open on the first category that actually has products.
        setSelected((list.find((c) => c.product_count > 0) || list[0] || {}).id || '');
      } catch (e) {
        console.warn(e);
        if (alive) setSelected(FINOS_CATEGORIES[0].id);
      } finally {
        if (alive) setLoadingCats(false);
      }
    })();
    apiClient.get('/products/stats').then((r) => alive && setStats(r.data)).catch(() => {});
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (!selected) return undefined;
    let alive = true;
    setLoadingProducts(true);
    apiClient
      .get(`/products/${selected}`)
      .then((r) => alive && setProducts(r.data))
      .catch(() => alive && setProducts([]))
      .finally(() => alive && setLoadingProducts(false));
    return () => { alive = false; };
  }, [selected]);

  const current = categories.find((c) => c.id === selected);
  const columns = useMemo(() => [
    { title: 'Bank', render: (p) => <strong>{p.bank}</strong>, value: (p) => p.bank },
    { title: 'Product', render: (p) => show(p.product), value: (p) => String(p.product || '') },
    { title: 'Rate', numeric: true, render: (p) => <span style={{ color: 'var(--secondary)', fontWeight: 700 }}>{show(p.rate)}</span>, value: (p) => String(show(p.rate)) },
    { title: 'Fee', numeric: true, render: (p) => show(p.fee), value: (p) => String(show(p.fee)) },
    { title: 'Tenure', numeric: true, render: (p) => show(p.tenure), value: (p) => String(show(p.tenure)) },
  ], []);

  return (
    <section className="demo-engine" aria-labelledby="catalogue-title">
      <div className="demo-header">
        <div>
          <h2 id="catalogue-title">Product Catalogue</h2>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>Pick a category to see the products your customers can compare.</p>
        </div>
        <div className="live-badge"><i className="fas fa-circle"></i> Live from finOS</div>
      </div>

      <div className="category-multi-grid" role="group" aria-label="Product categories" style={{ marginBottom: 16 }}>
        {categories.map((cat) => {
          const on = cat.id === selected;
          return (
            <button
              key={cat.id}
              type="button"
              className={`category-multi-card${on ? ' selected' : ''}`}
              onClick={() => setSelected(cat.id)}
              aria-pressed={on}
              disabled={loadingCats}
            >
              <span className="icon">{cat.icon || '📦'}</span>
              <span className="label">{cat.label}</span>
              {typeof cat.product_count === 'number' && (
                <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>{cat.product_count} products</span>
              )}
              <span className="check">{on ? '✓' : ''}</span>
            </button>
          );
        })}
      </div>

      <h3 style={{ fontSize: '1.05rem', margin: '0 0 10px' }}>{current ? current.label : 'Products'}</h3>
      {loadingProducts ? (
        <p style={{ color: 'var(--text-muted)', padding: '24px 0' }} role="status">Loading products…</p>
      ) : (
        <DataGrid rows={products} columns={columns} order={[[0, 'asc']]} pageLength={10} emptyText="No products in this category yet." />
      )}

      {stats && (
        <div className="demo-stats">
          <span><strong>{stats.total_banks}</strong> banks</span>
          <span><strong>{stats.total_products}</strong> products in total</span>
        </div>
      )}
    </section>
  );
};

export default ProductCatalogue;
